import {
  type PlayerOrgRatingAllResponsePagedData,
  type PlayerProfileResponse,
} from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/hocs/withValidUrl';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import { getIdFromParam } from '@on3/ui-lib/utils/url';
import { PlayerAllRankingPage } from '@public/components/Db/Profile/PlayerRating/PlayerAllRankingPage';
import { AdSidebarLayout } from '@public/components/Layouts/AdSidebarLayout';
import { PageWrapper } from '@public/components/Layouts/PageWrapper';
import { AthleteProvider } from '@public/contexts/AthleteProvider';
import { createTitle } from '@public/utils/meta';
import { getSportKeyFromPage, playerModules } from '@public/utils/player';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

interface IPlayerAllRankingPageProps extends IGenericPageProps {
  basicInfo: PlayerProfileResponse;
  allRankingList?: PlayerOrgRatingAllResponsePagedData | null;
}

const PlayerProfileRankings = ({
  allRankingList,
  basicInfo,
  ...props
}: IPlayerAllRankingPageProps) => {
  const data = useMemo(() => {
    return { athleteData: basicInfo || undefined };
  }, [basicInfo]);

  const router = useRouter();
  const sport = router.query.sport as string;
  const capitalizeSport =
    sport?.charAt(0)?.toUpperCase() + sport?.slice(1) || '';

  const title = createTitle(
    basicInfo?.name && `${basicInfo?.name}'s`,
    capitalizeSport,
    'Recruiting Industry Comparison',
  );
  const description = `${basicInfo?.name}'s rankings breakdown on the 4 major recruiting services: On3, 247sports, ESPN, and Rivals.`;

  //don't index if a player doesn't have an industry rating
  const noIndex = !allRankingList?.list?.[0]?.ratings?.find(
    (rank) => rank?.type === 'Consensus',
  )?.rating;

  return (
    <AthleteProvider data={data}>
      <PageWrapper
        {...props}
        description={description}
        noIndex={noIndex}
        title={title}
      >
        <AdSidebarLayout>
          <PlayerAllRankingPage allRankingList={allRankingList} />
        </AdSidebarLayout>
      </PageWrapper>
    </AthleteProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const { id, sport } = ctx.query as { id: string; sport?: string };
  const personKey = getIdFromParam(id);

  if (!personKey) {
    return {
      notFound: true,
    };
  }

  try {
    const [player] = await Promise.allSettled([
      playerModules.player(proxyApi, { personKey }),
    ]);

    const fetchedPlayer = getFulfilledValue(player);

    if (!fetchedPlayer) {
      return {
        notFound: true,
      };
    }

    const sportKey = getSportKeyFromPage(fetchedPlayer, sport);

    const [allRankingList] = await Promise.allSettled([
      playerModules.rankings.all(proxyApi, {
        personKey,
        sportKey,
      }),
    ]);

    const data = {
      pageType: 'database',
      siteData,
      userData: userData || {},
      basicInfo: fetchedPlayer,
      allRankingList: getFulfilledValue(allRankingList),
    };

    return { props: data };
  } catch (e) {
    return { props: {} };
  }
};

export default withValidUrl(PlayerProfileRankings);
